<template>
  <Modal
    v-if="isVisible"
    :show="isVisible"
    :title="t('Adicionar área de atuação')"
    :close-label="t('Fechar')"
    elem-id="modalLocationSelection"
    @close="close"
    @dismiss="close"
    >
      

    <div>
      <p>{{ t('Selecione o país para adicionar à franquia') }}:</p>

      <!-- Search filter -->
      <div class="form-group">
        <label for="searchLocation">{{ t('Procurar') }}:</label>
        <input-text
          data-testid="franquias-locations-selection-search"
          elem-id="franquias_selectionSearchFilter"
          id="searchLocation"
          v-model="searchFilter"
          :placeholder="t('Digite para filtrar')"
          style="width: 100%;" />
      </div>

      <!-- Loading state -->
      <inline-message v-if="isLoading" severity="info" :closable="false">
        {{ t('Carregando países') }}...
      </inline-message>

      <!-- Paises list -->
      <table v-else class="table table-hover table-striped table-bordered" style="max-height: 400px; overflow-y: auto;">
        <tbody>
          <tr v-for="pais in filteredPaises" :key="pais.ID">
            <td style="width: 75%">
              {{ pais.Name }}
            </td>
            <td style="width: 25%">
              <Button
                :data-testid="`franquias-locations-see-states-${pais.ID}`"
                icon="pi pi-eye"
                :label="t('Ver estados')"
                size="small"
                severity="info"
                @click="handleSeeStates(pais.ID, pais.Name)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalidades } from '../../composables/useLocalidades';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface PaisItem {
  ID?: Id;
  Name?: string;
  [key: string]: unknown;
}

interface FranquiaData {
  [key: string]: unknown;
}

interface ModalOpenPayload {
  franquiaId?: Id;
  franquiaData?: FranquiaData;
}

interface Props {
  franquiaId?: number | null;
  franquiaData?: FranquiaData | null;
}

withDefaults(defineProps<Props>(), {
  franquiaId: null,
  franquiaData: null,
});

const { t } = useI18n();
const { fetchPaises } = useLocalidades();

// Reactive data
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const paises = ref<PaisItem[]>([]);
const searchFilter = ref<string>('');
const currentFranquiaId = ref<Id | null>(null);
const currentFranquiaData = ref<FranquiaData | null>(null);

// Computed
const filteredPaises = computed<PaisItem[]>(() => {
  if (!searchFilter.value) {
    return paises.value;
  }
  const filter = searchFilter.value.toLowerCase();
  return paises.value.filter((pais) =>
    (pais.Name || '').toLowerCase().includes(filter),
  );
});

const loadPaises = async (): Promise<void> => {
  isLoading.value = true;
  try {
    paises.value = (await fetchPaises()) as PaisItem[];
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao carregar países'));
  } finally {
    isLoading.value = false;
  }
};

// Methods
const open = async (): Promise<void> => {
  isVisible.value = true;
  searchFilter.value = '';
  await loadPaises();
};

const close = (): void => {
  isVisible.value = false;
  searchFilter.value = '';
  paises.value = [];
};

// Event handlers for centralized ModalManager
const handleModalOpen = (data?: ModalOpenPayload): void => {
  if (data?.franquiaId) {
    currentFranquiaId.value = data.franquiaId;
  }
  if (data?.franquiaData) {
    currentFranquiaData.value = data.franquiaData;
  }
  void open();
};

const handleSeeStates = (paisId: Id | undefined, paisName: string | undefined): void => {
  // Emit event via eventBus to open states modal (centralized modal pattern)
  eventBus.emit('modal.locations-states.open', {
    paisId: paisId,
    paisName: paisName,
    franquiaId: currentFranquiaId.value,
    franquiaData: currentFranquiaData.value,
  });

  // Close this modal
  close();
};

// Lifecycle hooks for centralized ModalManager
onMounted(() => {
  eventBus.on('modal.locations-selection.open', handleModalOpen);
  eventBus.on('modal.locations-selection.close', close);
});

onBeforeUnmount(() => {
  eventBus.off('modal.locations-selection.open', handleModalOpen);
  eventBus.off('modal.locations-selection.close', close);
});

// Expose methods for parent
defineExpose({
  open,
  close,
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

table {
  display: block;
  max-height: 400px;
  overflow-y: auto;
}

/* PrimeVue components handle their own styling */
</style>
